import {
	Anchor,
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { dismissTermsNotice } from "client/Api/Terms/termsNotice";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { TermsNoticeKind } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	getTermsArticles,
	TermsArticle,
	TermsArticles,
	TRUST_CENTER_SUPPORT_EMAIL_ADDRESS
} from "./termsArticles";
import { TermsArticleLinkList } from "./TermsArticleLinkList";
import styles from "./TermsNoticeDialog.module.less";

export type TermsNoticeDialogProps = {
	kind: TermsNoticeKind;
	onDismissed(): void;
};

export const TermsNoticeDialog = (props: TermsNoticeDialogProps): ReactElement => {
	const { kind, onDismissed } = props;

	const hasUpdatedTerms: boolean = kind === TermsNoticeKind.UpdatedTerms;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const articles: readonly TermsArticle[] = useMemo(
		(): readonly TermsArticle[] => {
			const termsArticles: TermsArticles = getTermsArticles();

			return [
				termsArticles.customerLicenceAgreement,
				termsArticles.dataProcessingAgreement,
				termsArticles.serviceLevelAgreement,
				termsArticles.acceptableUsePolicy
			];
		},
		[]
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const dismissHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				await dismissTermsNotice(globalCancellation);
				onDismissed();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			isBusy,
			onDismissed
		]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="terms-notice-dialog"
		>
			<Dialog.Header
				title={hasUpdatedTerms
					? localize("_UpdatedTermsForWorkspace365")
					: localize("_TermsForWorkspace365")
				}
			/>
			<Dialog.Body>
				<div className={styles.content}>
					{hasUpdatedTerms
						? (
							<>
								<p>{localize("_UpdatedTermsNoticeWhatChangesInfo")}</p>
								<p>{localize("_UpdatedTermsNoticeNoActionNeededInfo")}</p>
							</>
						)
						: <p>{localize("_TermsNoticeWhatAppliesInfo")}</p>
					}
					<TermsArticleLinkList articles={articles} />
					<p>{localize("_UpdatedTermsNoticeWhereToFindInfo")}</p>
					<p>
						<FormattedText
							text={localize("_TermsQuestionsInfo")}
							thingsToReplace={[
								{
									what: "{{EmailAnchor}}",
									filler: (
										<Anchor
											href={`mailto:${TRUST_CENTER_SUPPORT_EMAIL_ADDRESS}`}
											trackingId="support-email-link"
										>
											{TRUST_CENTER_SUPPORT_EMAIL_ADDRESS}
										</Anchor>
									)
								}
							]}
						/>
					</p>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_GotIt")}
						onClick={dismissHandler}
						trackingId="got-it"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
TermsNoticeDialog.displayName = "TermsNoticeDialog";
